
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ImageIcon, TestTube2, FileText, Palette } from "lucide-react"; // Import icons for print types
import { getPreviewUrl } from "@/utils/imageUtils";
import { Badge } from "@/components/ui/badge"; // Import Badge component for visual distinction

interface PrintsTableProps {
  prints: any[];
  onPrintClick: (print: any) => void;
}

export function PrintsTable({ prints, onPrintClick }: PrintsTableProps) {
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'in_cart':
        return 'bg-blue-50 hover:bg-blue-100';
      case 'waiting_to_print':
        return 'bg-yellow-50 hover:bg-yellow-100';
      case 'printing':
        return 'bg-orange-50 hover:bg-orange-100';
      case 'printed':
        return 'bg-green-50 hover:bg-green-100';
      case 'cancelled':
        return 'bg-red-50 hover:bg-red-100';
      default:
        return '';
    }
  };

  const isPrintType = (print: any, type: 'test' | 'sample') => {
    if (!print.caption) return false;
    return type === 'test' 
      ? print.caption.toLowerCase().includes('test print')
      : print.caption.toLowerCase().includes('sample print');
  };

  const getPrintTypeIcon = (print: any) => {
    if (isPrintType(print, 'test')) {
      return <TestTube2 size={16} className="text-purple-500" />;
    } else if (isPrintType(print, 'sample')) {
      return <Palette size={16} className="text-blue-500" />;
    } else {
      return <FileText size={16} className="text-green-600" />;
    }
  };

  const getPrintTypeLabel = (print: any) => {
    if (isPrintType(print, 'test')) {
      return <Badge variant="outline" className="border-purple-500 text-purple-700">Test Print</Badge>;
    } else if (isPrintType(print, 'sample')) {
      return <Badge variant="outline" className="border-blue-500 text-blue-700">Sample Print</Badge>;
    }
    return null;
  };

  const getArtworkInfo = (print: any) => {
    // Check if this is a standard print with edition and artwork information
    if (print.editions?.artworks) {
      return {
        title: print.editions?.artworks?.title,
        creator: print.editions?.artworks?.creators,
        preview: print.editions?.artworks?.preview,
        dimensions: `${print.editions?.width} x ${print.editions?.height} cm`,
      };
    } 
    // Special case for test/sample prints that might have artwork info via highres_file
    else if (print.highres_file?.artworks && print.highres_file.artworks[0]) {
      const artwork = print.highres_file.artworks[0];
      return {
        title: print.caption || artwork.title,
        creator: artwork.creators,
        preview: artwork.preview,
        dimensions: print.width && print.height ? `${print.width} x ${print.height} cm` : '',
      };
    }
    // Fallback case for prints with no artwork information
    else {
      return {
        title: print.caption || 'Untitled Print',
        creator: null,
        preview: null,
        dimensions: print.width && print.height ? `${print.width} x ${print.height} cm` : '',
      };
    }
  };

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader className="text-xs">
          <TableRow>
            <TableHead className="w-16">Preview</TableHead>
            <TableHead>Print Type</TableHead>
            <TableHead>Artwork / Edition</TableHead>
            <TableHead>Edition</TableHead>
            <TableHead>Certification</TableHead>
            <TableHead>Media</TableHead>
            <TableHead>Print Provider</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="text-xs">
          {prints?.map((print) => {
            const artworkInfo = getArtworkInfo(print);
            
            return (
              <TableRow 
                key={print.print_id}
                className={`${getStatusStyle(print.status)} ${print.certificate_id ? "bg-opacity-60" : ""} cursor-pointer`}
                onClick={() => onPrintClick(print)}
              >
                <TableCell className="w-16 p-2">
                  {artworkInfo.preview ? (
                    <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-muted">
                      <img
                        src={getPreviewUrl(artworkInfo.preview)}
                        alt={artworkInfo.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    {getPrintTypeIcon(print)}
                    {getPrintTypeLabel(print)}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    {artworkInfo.title}
                    {print.editions && print.editions.edition_name && (
                      <> / {print.editions.edition_name}</>
                    )}
                    {artworkInfo.dimensions && (
                      <span className="text-muted-foreground ml-1">
                        ({artworkInfo.dimensions})
                      </span>
                    )}
                  </div>
                  <div className="italic text-muted-foreground">
                    {artworkInfo.creator?.nick_name ? (
                      `${artworkInfo.creator.nick_name} / ${artworkInfo.creator.name} ${artworkInfo.creator.surname}`
                    ) : artworkInfo.creator ? (
                      `${artworkInfo.creator.name} ${artworkInfo.creator.surname}`
                    ) : ''}
                  </div>
                </TableCell>
                <TableCell>
                  {print.edition_number ? (
                    <>
                      #{print.edition_number}
                      {print.editions?.edition_limit && `/${print.editions.edition_limit}`}
                    </>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  {print.certified ? (
                    print.certificates?.hologram_number ? (
                      <span className={`${print.certificate_id ? 'bg-green-500' : 'bg-sky-500'} text-white px-2 py-1 rounded-md font-medium`}>
                        {print.certificates.hologram_number}
                      </span>
                    ) : (
                      <span className="bg-sky-500 text-white px-2 py-1 rounded-md font-medium">
                        CERTIFIED
                      </span>
                    )
                  ) : (
                    "Not Required"
                  )}
                </TableCell>
                <TableCell>
                  {print.media?.media_name || "Not specified"}
                </TableCell>
                <TableCell>
                  {print.print_service_providers?.name || "Not specified"}
                </TableCell>
                <TableCell>
                  <span className="capitalize">
                    {print.status?.replace(/_/g, ' ') || "Not specified"}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
